import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Card,
  Collapse,
  Divider,
  Flex,
  Heading,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionFolderValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { CheckIcon, TriangleAlertIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { FolderIssuesCard } from './FolderIssuesCard';
import { FolderValidation } from './utils/folderIssues.utils';
import {
  getVisibleIssuesCount,
  groupIngestionDocuments,
} from './utils/ingestionDocuments.utils';
import {
  getContractDocDisplayName,
  getValidationResultsForDocument,
  ValidationItem,
} from './utils/validation.utils';

interface ComplianceOverviewProps {
  documents: ExternalIngestionDocumentFragment[];
  validationData: ExternalValidationReportDataFragment | null;
  hiddenFolderIssues?: IngestionFolderValidationErrorReviewFragment[];
  userAddedFolderIssues?: IngestionFolderValidationErrorReviewFragment[];
  userAddedDocumentIssues?: IngestionDocumentValidationErrorReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  onDocumentSelect?: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
}

export const ComplianceOverview: FC<ComplianceOverviewProps> = ({
  documents,
  validationData,
  hiddenFolderIssues,
  userAddedFolderIssues,
  userAddedDocumentIssues,
  hiddenDocumentValidationReviews,
  submittedDocumentReviews,
  onDocumentSelect,
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );

  // Group user-added document issues by contract document
  const userAddedIssuesByContractDoc = useMemo(() => {
    const groupedIssues = new Map<
      string,
      IngestionDocumentValidationErrorReviewFragment[]
    >();

    (userAddedDocumentIssues || []).forEach((issue) => {
      // Only include issues that have a contractDocumentUuid
      if (issue.contractDocumentUuid) {
        const contractDocId = issue.contractDocumentUuid;
        if (!groupedIssues.has(contractDocId)) {
          groupedIssues.set(contractDocId, []);
        }
        const issues = groupedIssues.get(contractDocId);
        if (issues) {
          issues.push(issue);
        }
      }
    });

    return groupedIssues;
  }, [userAddedDocumentIssues]);

  // Get all grouped documents in order (similar to ComplianceDocumentsSidebar)
  const groupedDocuments = useMemo(() => {
    return groupIngestionDocuments(
      documents,
      validationData,
      submittedDocumentReviews
    );
  }, [documents, validationData, submittedDocumentReviews]);

  // Create ordered list of all documents to display (same group order as sidebar)
  // The groupIngestionDocuments function already applies proper sorting within each group
  const allDocumentsOrdered = useMemo(() => {
    return [
      ...groupedDocuments.needsReviewDocuments,
      ...groupedDocuments.otherDocuments,
      ...groupedDocuments.archivedDocuments,
      ...groupedDocuments.uncheckedDocuments,
      ...groupedDocuments.blankDocuments,
    ];
  }, [groupedDocuments]);

  // Group validation results by ingestion document, then by contract document
  const validationResultsByIngestionDoc = useMemo(() => {
    if (!validationData) return new Map();

    const resultsByIngestionDoc = new Map<
      string,
      {
        ingestionDocument: ExternalIngestionDocumentFragment;
        contractResults: Map<
          string,
          {
            contractDocument: ExternalIngestionDocumentFragment['contractDocuments'][number];
            errors: ValidationItem[];
            successes: ValidationItem[];
            userAddedIssues: IngestionDocumentValidationErrorReviewFragment[];
          }
        >;
      }
    >();

    allDocumentsOrdered.forEach((ingestionDocument) => {
      if (!ingestionDocument.contractDocuments) return;

      const contractResults = new Map<
        string,
        {
          contractDocument: ExternalIngestionDocumentFragment['contractDocuments'][number];
          errors: ValidationItem[];
          successes: ValidationItem[];
          userAddedIssues: IngestionDocumentValidationErrorReviewFragment[];
        }
      >();

      ingestionDocument.contractDocuments.forEach((contractDoc) => {
        const { errors, successes } = getValidationResultsForDocument(
          ingestionDocument,
          validationData,
          contractDoc.id
        );

        const userAddedIssues =
          userAddedIssuesByContractDoc.get(contractDoc.id) || [];

        if (
          errors.length > 0 ||
          successes.length > 0 ||
          userAddedIssues.length > 0
        ) {
          contractResults.set(contractDoc.id, {
            contractDocument: contractDoc,
            errors,
            successes,
            userAddedIssues,
          });
        }
      });

      if (contractResults.size > 0) {
        resultsByIngestionDoc.set(ingestionDocument.id, {
          ingestionDocument,
          contractResults,
        });
      }
    });

    return resultsByIngestionDoc;
  }, [allDocumentsOrdered, validationData, userAddedIssuesByContractDoc]);

  const folderValidation =
    validationData?.folderValidation as unknown as FolderValidation;

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }

      return newSet;
    });
  };

  const handleDocumentClick = (
    ingestionDocument: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => {
    if (onDocumentSelect) {
      onDocumentSelect(ingestionDocument, contractDocumentId);
    }
  };

  return (
    <Box height="100%" overflow="auto" p={6}>
      <VStack align="stretch" spacing={12}>
        <VStack alignItems="flex-start" spacing={8}>
          <FolderIssuesCard
            folderValidation={folderValidation}
            hiddenIssues={hiddenFolderIssues}
            userAddedIssues={userAddedFolderIssues}
            variant="external"
          />

          {(folderValidation?.errorCount || 0) > 0 && <Divider mb={-4} />}
        </VStack>

        {/* Ingestion Documents with Contract Document Validation Results */}
        {Array.from(validationResultsByIngestionDoc.entries()).map(
          ([ingestionDocId, ingestionDocResult]) => {
            const { ingestionDocument, contractResults } =
              ingestionDocResult as {
                ingestionDocument: ExternalIngestionDocumentFragment;
                contractResults: Map<
                  string,
                  {
                    contractDocument: ExternalIngestionDocumentFragment['contractDocuments'][number];
                    errors: ValidationItem[];
                    successes: ValidationItem[];
                    userAddedIssues: IngestionDocumentValidationErrorReviewFragment[];
                  }
                >;
              };

            return (
              <VStack
                key={ingestionDocId as string}
                align="stretch"
                spacing={6}
              >
                {/* Ingestion Document Header */}
                <Box
                  _hover={{
                    color: 'compliance.text.primary',
                  }}
                  color="compliance.text.secondary"
                  cursor="pointer"
                  onClick={() => {
                    // If there's only one contract document, click it directly
                    if (ingestionDocument.contractDocuments?.length === 1) {
                      handleDocumentClick(
                        ingestionDocument,
                        ingestionDocument.contractDocuments[0].id
                      );
                    } else {
                      // For multiple contract docs, just select the document
                      handleDocumentClick(ingestionDocument);
                    }
                  }}
                >
                  <Heading fontSize="18px">
                    {ingestionDocument.documentName}
                  </Heading>
                </Box>

                {/* Contract Documents under this Ingestion Document */}
                {Array.from(contractResults.entries()).map(
                  ([contractDocId, contractResult]) => {
                    const {
                      contractDocument,
                      errors,
                      successes,
                      userAddedIssues,
                    } = contractResult;
                    const contractDocName =
                      getContractDocDisplayName(contractDocument);

                    return (
                      <VStack key={contractDocId} align="stretch" spacing={4}>
                        {/* Contract Document Header */}
                        <Heading
                          _hover={{
                            color: 'compliance.text.primary',
                          }}
                          color="compliance.text.secondary"
                          cursor="pointer"
                          fontSize="14px"
                          letterSpacing="wider"
                          ml={4}
                          textTransform="uppercase"
                          onClick={() => handleDocumentClick(ingestionDocument)}
                        >
                          {contractDocName}
                        </Heading>

                        {/* Issues Section for this contract document */}
                        {(errors.length > 0 || userAddedIssues.length > 0) && (
                          <Card ml={4} px={5} py={4}>
                            <VStack align="stretch" spacing={2}>
                              <Flex
                                align="center"
                                cursor="pointer"
                                justifyContent="space-between"
                                onClick={() =>
                                  toggleSection(`${contractDocId}-issues`)
                                }
                              >
                                <Heading
                                  color="compliance.card.header.text"
                                  fontSize="12px"
                                  textTransform="uppercase"
                                >
                                  Validation Issues (
                                  {getVisibleIssuesCount(
                                    errors,
                                    userAddedIssues,
                                    contractDocId,
                                    hiddenDocumentValidationReviews || []
                                  )}
                                  )
                                </Heading>
                                {collapsedSections.has(
                                  `${contractDocId}-issues`
                                ) ? (
                                  <ChevronRightIcon />
                                ) : (
                                  <ChevronDownIcon />
                                )}
                              </Flex>
                              <Collapse
                                in={
                                  !collapsedSections.has(
                                    `${contractDocId}-issues`
                                  )
                                }
                              >
                                <VStack align="stretch" spacing={2}>
                                  {/* User Added Issues - displayed first */}
                                  {userAddedIssues.map((issue) => (
                                    <Flex key={issue.id} align="center" gap={2}>
                                      <Text
                                        bg="compliance.tag.bg"
                                        borderRadius="xs"
                                        fontSize="xs"
                                        px={2}
                                        py={1}
                                        textTransform="uppercase"
                                      >
                                        Custom
                                      </Text>
                                      <Text fontSize="sm" fontWeight="medium">
                                        {issue.message || 'User added issue'}
                                      </Text>
                                    </Flex>
                                  ))}

                                  {/* Regular validation errors */}
                                  {errors.map((error, errorIndex) => {
                                    // Check if this validation item is hidden
                                    const isHidden = (
                                      hiddenDocumentValidationReviews || []
                                    ).some(
                                      (review) =>
                                        review.ruleUuid === error.ruleUuid &&
                                        review.contractDocumentUuid ===
                                          contractDocId &&
                                        review.isHidden
                                    );

                                    const iconColor = isHidden
                                      ? 'compliance.text.muted'
                                      : 'compliance.status.error';

                                    return (
                                      <Flex
                                        key={
                                          error.ruleUuid ??
                                          `${contractDocId}-${errorIndex}`
                                        }
                                        align="center"
                                        gap={2}
                                        opacity={isHidden ? 0.6 : 1}
                                      >
                                        <Icon
                                          as={TriangleAlertIcon}
                                          color={iconColor}
                                          flexShrink={0}
                                        />
                                        <Text
                                          color={
                                            isHidden
                                              ? 'compliance.text.muted'
                                              : undefined
                                          }
                                          fontSize="sm"
                                          fontWeight="medium"
                                          textDecoration={
                                            isHidden
                                              ? 'line-through'
                                              : undefined
                                          }
                                        >
                                          {error.message}
                                        </Text>
                                      </Flex>
                                    );
                                  })}
                                </VStack>
                              </Collapse>
                            </VStack>
                          </Card>
                        )}

                        {/* Successful Checks Section for this contract document */}
                        {successes.length > 0 && (
                          <Card ml={4} px={5} py={4}>
                            <VStack align="stretch" spacing={2}>
                              <Flex
                                align="center"
                                cursor="pointer"
                                justifyContent="space-between"
                                onClick={() =>
                                  toggleSection(`${contractDocId}-successes`)
                                }
                              >
                                <Heading
                                  color="compliance.card.header.text"
                                  fontSize="12px"
                                  textTransform="uppercase"
                                >
                                  Successful Checks ({successes.length})
                                </Heading>
                                {collapsedSections.has(
                                  `${contractDocId}-successes`
                                ) ? (
                                  <ChevronRightIcon />
                                ) : (
                                  <ChevronDownIcon />
                                )}
                              </Flex>
                              <Collapse
                                in={
                                  !collapsedSections.has(
                                    `${contractDocId}-successes`
                                  )
                                }
                              >
                                <VStack align="stretch" spacing={2}>
                                  {successes.map((success, successIndex) => (
                                    <Flex
                                      key={successIndex}
                                      align="center"
                                      gap={2}
                                    >
                                      <Icon
                                        as={CheckIcon}
                                        color="compliance.status.success"
                                        flexShrink={0}
                                      />
                                      <Text fontSize="sm">
                                        {success.message}
                                      </Text>
                                    </Flex>
                                  ))}
                                </VStack>
                              </Collapse>
                            </VStack>
                          </Card>
                        )}
                      </VStack>
                    );
                  }
                )}
              </VStack>
            );
          }
        )}

        {validationResultsByIngestionDoc.size === 0 && (
          <Box p={4}>
            <Text color="compliance.text.muted" textAlign="center">
              No validation issues found in documents that need review.
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
